import { useState } from "react";
import styles from "./Admin.module.css";

export interface TabDefinition {
  id: string;
  label: string;
  content: React.ReactNode;
}

/**
 * A simple single-level tab strip (`QR-01`: no nested menus) — one tab's content mounts at a time.
 *
 * A page that deep-links its tabs passes `activeId` and `onSelect`, so the address decides which tab shows. Without them the strip keeps its own state.
 */
export function Tabs({ tabs, activeId: controlledId, onSelect }: { tabs: TabDefinition[]; activeId?: string; onSelect?: (id: string) => void }): React.JSX.Element {
  const [ownId, setOwnId] = useState(tabs[0]?.id);
  const activeId = controlledId ?? ownId;

  return (
    <div>
      <div role="tablist" className={styles.tabList}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`tab-${tab.id}`}
            aria-selected={tab.id === activeId}
            aria-controls={`tabpanel-${tab.id}`}
            className={tab.id === activeId ? styles.tabActive : styles.tab}
            onClick={() => (onSelect ? onSelect(tab.id) : setOwnId(tab.id))}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {tabs.map(
        (tab) =>
          tab.id === activeId && (
            <div key={tab.id} id={`tabpanel-${tab.id}`} role="tabpanel" aria-labelledby={`tab-${tab.id}`}>
              {tab.content}
            </div>
          ),
      )}
    </div>
  );
}
